import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export function PageHero({
  eyebrow,
  title,
  subtitle,
  className,
  children,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cn('container-page py-12 md:py-16', className)}>
      <div className="max-w-prose animate-fade-in">
        {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
        <h1 className="font-display text-display-sm font-extrabold text-ink md:text-display">
          {title}
        </h1>
        {subtitle && <p className="mt-4 text-lg text-ink-muted">{subtitle}</p>}
        {children && <div className="mt-8">{children}</div>}
      </div>
    </div>
  );
}

/** Generic empty / not-yet-built state (spec §16: empty states everywhere). */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'card flex flex-col items-center justify-center gap-4 px-6 py-16 text-center',
        className,
      )}
    >
      {icon && (
        <div className="grid h-14 w-14 place-items-center rounded-2xl bg-surface-raised text-brand-soft">
          {icon}
        </div>
      )}
      <div className="space-y-1.5">
        <h2 className="font-display text-xl font-bold text-ink">{title}</h2>
        {description && <p className="mx-auto max-w-md text-sm text-ink-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/** Phase-stub page used until a route is implemented. */
export function PagePlaceholder({
  eyebrow,
  title,
  subtitle,
  phase,
  children,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  phase?: string;
  children?: ReactNode;
}) {
  return (
    <PageHero eyebrow={eyebrow} title={title} subtitle={subtitle}>
      <div className="space-y-6">
        {phase && (
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-ink-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" />
            Arriving in {phase}
          </span>
        )}
        {children}
      </div>
    </PageHero>
  );
}
